import { useEffect, useState } from 'react';
import { FALLBACK_PROVIDERS, fetchProviders } from './auth/providers';
import type { IdentityProviderInfo } from './auth/providers';
import { beginLogin, clearSession, completeLogin, getSession } from './auth/session';
import type { Session } from './auth/session';
import { routeFor } from './state/route';
import type { Route, Tab } from './state/route';
import { AccessPanel } from './AccessPanel';
import { IdentityPanel } from './IdentityPanel';
import { PurchaseCancelledPanel } from './PurchaseCancelledPanel';
import { PurchaseSuccessPanel } from './PurchaseSuccessPanel';
import { ShopPanel } from './ShopPanel';

/**
 * Tilgong Test Merchant — the buyer-facing verification storefront (ADR-022). The header runs
 * the real platform sign-in (ADR-017/018): one "Sign in with …" button per identity provider
 * the auth server advertises, each a code+PKCE flow; the shop itself browses anonymously.
 * Three tabs (Shop / Identity / Access) plus the checkout return routes
 * (/purchase/success, /purchase/cancelled). This file is the composition shell
 * (unit-coverage-excluded like main.tsx) — flows are covered by the Playwright E2E layer;
 * the logic units live in src/auth, src/api, src/purchase, and src/state.
 */
export function App() {
  const [route, setRoute] = useState<Route>(routeFor(window.location.pathname));
  const [tab, setTab] = useState<Tab>('shop');
  const [session, setSessionState] = useState<Session | null>(getSession());
  const [authError, setAuthError] = useState<string | null>(null);
  const [providers, setProviders] = useState<readonly IdentityProviderInfo[]>(FALLBACK_PROVIDERS);

  // The sign-in options follow the auth server's enabled-provider catalog (ADR-018).
  useEffect(() => {
    if (session !== null) return;
    void fetchProviders().then(setProviders);
  }, [session]);

  // Finish the redirect leg when the auth server sends the browser back.
  useEffect(() => {
    if (route !== 'auth-callback') return;
    void (async () => {
      try {
        const established = await completeLogin(new URLSearchParams(window.location.search));
        setSessionState(established);
      } catch (e) {
        setAuthError(e instanceof Error ? e.message : String(e));
      } finally {
        window.history.replaceState({}, '', '/');
        setRoute('shop');
      }
    })();
  }, [route]);

  function signIn(idp: string): void {
    setAuthError(null);
    beginLogin(idp).catch((e: unknown) => setAuthError(e instanceof Error ? e.message : String(e)));
  }

  function signOut(): void {
    clearSession();
    setSessionState(null);
  }

  function backToShop(): void {
    window.history.replaceState({}, '', '/');
    setRoute('shop');
    setTab('shop');
  }

  const tabButton = (t: Tab, label: string) => (
    <button
      onClick={() => setTab(t)}
      disabled={tab === t}
      style={{ fontWeight: tab === t ? 'bold' : 'normal' }}
    >
      {label}
    </button>
  );

  return (
    <main style={{ fontFamily: 'system-ui, sans-serif', maxWidth: 880, margin: '2rem auto', padding: '0 1rem' }}>
      <h1>Tilgong Test Merchant</h1>

      {authError !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {authError}
        </p>
      )}

      <div
        style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid #ddd', paddingBottom: '0.75rem' }}
      >
        {session === null ? (
          <>
            <span>Browsing anonymously.</span>
            {providers.map((provider) => (
              <button key={provider.id} onClick={() => signIn(provider.id)}>
                Sign in with {provider.displayName}
              </button>
            ))}
          </>
        ) : (
          <>
            <span>
              Signed in as <strong>{session.email ?? 'unknown'}</strong>
            </span>
            <button type="button" onClick={signOut}>
              Sign out
            </button>
          </>
        )}
      </div>

      {route === 'auth-callback' ? (
        <p>Completing sign-in…</p>
      ) : route === 'purchase-success' ? (
        <PurchaseSuccessPanel onBackToShop={backToShop} />
      ) : route === 'purchase-cancelled' ? (
        <PurchaseCancelledPanel onBackToShop={backToShop} />
      ) : (
        <>
          <nav style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0' }}>
            {tabButton('shop', 'Shop')}
            {tabButton('identity', 'Identity')}
            {tabButton('access', 'Access')}
          </nav>
          {tab === 'identity' ? (
            <IdentityPanel session={session} />
          ) : tab === 'access' ? (
            <AccessPanel />
          ) : (
            <ShopPanel />
          )}
        </>
      )}
    </main>
  );
}
